import { ActionPlanNetImpactBody, ScoreCard, TrajectoryCards, TrajectoryCardsProps, WhiteBox } from "@foodpilot/foods";
import { Stack, Typography } from "@mui/material";
import { Box, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { ScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { ActionPlanData } from "src/context/Actions/useActionPlanData";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { ScoreImprovement } from "src/types";
import { useGetAllActionPlanActions } from "src/api/client-api/ActionPlan/ActionPlanAction/useGetAllActionPlanAction";
import { Loading } from "src/components/utils/Loading";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { Suspense } from "src/components/utils/Suspense";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import {
    mapVariationsToScoreCardOptions,
    useActionPlanSelectorVariations,
} from "src/hooks/useActionPlanSelectorVariations";
import { scoreHasBaseline } from "src/utils/actionPlanScoreFilters";
import { ApproximateImpactTooltip } from "src/components/Actions/Common/ApproximateImpactTooltip";
import { getScorePrecision } from "src/context/Scores/provider";

type ScoreOptions = {
    rowId: number;
    label: string;
    unit: string;
    group: string;
    scoreId: number;
    onClick: () => void;
    improvement: ScoreImprovement;
};

export type ScoreComparisonCardProps = {
    selectedOption: ScoreOptions;

    position: TrajectoryCardsProps["position"];
    disableFooter?: boolean;
    hideGoalWhenEmpty?: boolean;
    actionPlanData: ActionPlanData;
    catalogSelector?: boolean;
};
export const ScoreComparisonCard = (props: ScoreComparisonCardProps) => {
    const {
        position,
        disableFooter = false,
        hideGoalWhenEmpty = false,
        selectedOption,
        actionPlanData,
        catalogSelector = false,
    } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const { scoresWithVariations, selectedVariations, lastSelectedVariation, getEntityScore, formatScore, getColor } =
        useEntityScores();
    const selectorVariations = useActionPlanSelectorVariations(scoresWithVariations);
    const { actionPlan, persistedOptions, isFetched, olapScoresQuery } = useActionsContext();

    // Extract data for API calls
    const actionPlanId = actionPlan?.id;
    const scoreId = selectedOption.scoreId;

    const { data: allActionPlanActions } = useGetAllActionPlanActions({
        actionPlanID: actionPlanId,
        scoresQuery: olapScoresQuery,
    });

    const selectedVariationId = selectedVariations.query?.[scoreId];
    const scoreVariationRowId = scoresWithVariations.find(
        (scoreWithVariation) => scoreWithVariation.scoreId === scoreId,
    )?.rowId;
    const selectedVariationRowId =
        scoresWithVariations.find((scoreWithVariation) => scoreWithVariation.variationId === selectedVariationId)
            ?.rowId ?? scoreVariationRowId;

    if (actionPlan === undefined || actionPlan === null) return <Loading />;

    const startPost = actionPlan.startSsqPost;
    const postWithoutPlan = actionPlan.actionPlanSsqPosts[actionPlan.actionPlanSsqPosts.length - 1].ssqPost;
    const postWithPlan = actionPlan.simulationSsqPosts[actionPlan.simulationSsqPosts.length - 1];

    const scoreEntity = getEntityScore(scoreId);
    const isNetCard = scoreEntity !== undefined && !scoreHasBaseline(scoreEntity);

    const variationsOptions =
        catalogSelector ?
            mapVariationsToScoreCardOptions(selectorVariations)
        :   mapVariationsToScoreCardOptions(
                scoresWithVariations.filter((scoreWithVariation) => scoreWithVariation.scoreId === scoreId),
            );

    const startingPost = {
        label: startPost.campaign?.label ?? actionPlanData.metadata.startingPost.scoreByLabel,
        score:
            startPost.olapScores[scoreId].score.value ??
            OlapScoresService.normalizeNumericalScore(actionPlanData.metadata.startingPost.score),
    };

    const targetWithoutPlan = {
        label: persistedOptions.withoutPlanName,
        score:
            postWithoutPlan.olapScores[scoreId]?.score.value ??
            OlapScoresService.normalizeNumericalScore(actionPlanData.metadata.scoresWithoutActionPost.lastItem?.score),
    };

    const totalImpact =
        allActionPlanActions?.reduce((agg, actionPlanAction) => {
            if (Array.isArray(actionPlanAction.totalImpact)) return agg;

            return agg + (actionPlanAction.totalImpact[scoreId] ?? 0);
        }, 0) ?? 0;

    const totalStartingScore = OlapScoresService.normalizeNumericalScore(startingPost.score);
    const totalScoreWithActionPlan = totalStartingScore !== null ? totalStartingScore + totalImpact : null;

    const targetWithPlan = {
        label: persistedOptions.withPlanName,
        score: totalScoreWithActionPlan,
    };

    const goal = actionPlanData.metadata.goal?.value;
    const unit =
        postWithoutPlan.olapScores[scoreId].unit ?? postWithPlan.olapScores[scoreId].unit ?? selectedOption.unit;
    const isGrowthDisabled = actionPlan?.simulationVaryPercentage === undefined;

    const cardData = formatData({
        position,
        isGrowthDisabled: isGrowthDisabled,
        steps: {
            startingPost,
            targetWithoutPlan,
            targetWithPlan,
        },
    });

    const startScore = OlapScoresService.normalizeNumericalScore(cardData[0]?.score);
    const endWithoutActionScore = OlapScoresService.normalizeNumericalScore(cardData[1]?.score);
    const endWithActionScore = OlapScoresService.normalizeNumericalScore(cardData[2]?.score);

    const isContentLoading = !isFetched && lastSelectedVariation.scoreId === scoreId;
    if (isNetCard) {
        const precision = getScorePrecision(scoreEntity);
        const netValueColor = getColor(totalImpact, { improvement: selectedOption.improvement, precision });
        return (
            <Box sx={{ width: "100%" }}>
                <WhiteBox sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <Stack
                        sx={{
                            backgroundColor: theme.custom.grey[200],
                            borderTopLeftRadius: theme.spacing(2),
                            borderTopRightRadius: theme.spacing(2),
                            borderBottom: `1px solid ${theme.custom.grey[500]}`,
                            padding: theme.spacing(2, 4),
                        }}
                    >
                        <Typography variant="h3">{selectedOption.group}</Typography>
                        <Typography variant="body-medium">{selectedOption.label}</Typography>
                    </Stack>
                    <Suspense isLoading={isContentLoading}>
                        <ApproximateImpactTooltip
                            value={totalImpact}
                            precision={precision}
                            unit={unit}
                            sx={{ display: "flex", flex: 1 }}
                        >
                            <ActionPlanNetImpactBody
                                message={t("action_plan.net_impact_label", "Net impact of your action plan")}
                                formattedValue={formatScore(totalImpact, { precision })}
                                unit={unit}
                                valueColor={netValueColor}
                            />
                        </ApproximateImpactTooltip>
                    </Suspense>
                </WhiteBox>
            </Box>
        );
    }

    return (
        <Box sx={{ width: "100%" }}>
            <ScoreCard
                header={{
                    selectedItemId: selectedVariationRowId,
                    options: variationsOptions,
                    variant: "secondary",
                }}
                disableFooter={disableFooter}
            >
                <Suspense isLoading={isContentLoading}>
                    <TrajectoryCards
                        goal={goal ?? null}
                        hideGoalWhenEmpty={hideGoalWhenEmpty}
                        unit={unit}
                        steps={{
                            start: startScore ?? null,
                            endWithoutAction: cardData[1] && (endWithoutActionScore ?? null),
                            endWithAction: endWithActionScore ?? null,
                        }}
                        textOptions={{
                            goalStep: t("component.goal.info"),
                            noGoalLabel: t("component.goal.no_goal"),
                            startingStep: cardData[0]?.label ?? "",
                            targetWithoutPlanStep: cardData[1]?.label ?? "",
                            targetWithPlanStep: cardData[2].label ?? "",
                        }}
                        position={position}
                        improvement={selectedOption.improvement}
                    />
                </Suspense>
            </ScoreCard>
        </Box>
    );
};

type Step = {
    label: string;
    score: ScoreValue;
};

type Options = {
    position: TrajectoryCardsProps["position"];
    isGrowthDisabled: boolean;
    steps: {
        startingPost: Step;
        targetWithoutPlan: Step;
        targetWithPlan: Step;
    };
};
const formatData = (options: Options): [Step | null, Step | undefined, Step] => {
    const {
        position,
        isGrowthDisabled,
        steps: { startingPost, targetWithPlan, targetWithoutPlan },
    } = options;

    // This segregates the logic from 3 different post, to the logic for 1st, 2nd and 3rd step from UI Card
    if (position === "horizontal") {
        if (isGrowthDisabled) {
            return [startingPost, undefined, targetWithPlan];
        }

        return [startingPost, targetWithoutPlan, targetWithPlan];
    }

    if (position === "vertical") {
        // Seems like whatever if position is vertical, it'll always be 2 items
        // And the POSITION 1, 2 OUT OF 0, 1, 2.
        // So I need to put 1st item as 2nd position in case growth is Disabled
        if (isGrowthDisabled) {
            return [null, startingPost, targetWithPlan];
        }

        return [null, targetWithoutPlan, targetWithPlan];
    }

    return [startingPost, targetWithoutPlan, targetWithPlan];
};
